import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Plus, Trash2, Pencil, Search, Package, Phone, MapPin, User, FileText, Tag } from "lucide-react";

const CATEGORIES = ["Bahan Baku", "Kemasan & Alat Sekali Pakai", "Sanitasi", "Administrasi", "Peralatan Dapur", "Peralatan Makan"];
const EMPTY = { material: "", category: CATEGORIES[0], supplier_name: "", supplier_address: "", sales_name: "", phone: "", sop: "" };

export function SupplierPanel() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");
  const [dlg, setDlg] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);

  const load = () => api.get("/suppliers").then((r) => setRows(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const openAdd = () => { setForm(EMPTY); setEditId(null); setDlg(true); };
  const openEdit = (r) => { setForm({ material: r.material, category: r.category || CATEGORIES[0], supplier_name: r.supplier_name || "", supplier_address: r.supplier_address || "", sales_name: r.sales_name || "", phone: r.phone || "", sop: r.sop || "" }); setEditId(r.id); setDlg(true); };
  const save = async () => {
    if (!form.material.trim()) { toast.error("Isi nama bahan/barang"); return; }
    try {
      if (editId) await api.put(`/suppliers/${editId}`, form); else await api.post("/suppliers", form);
      toast.success(editId ? "Data diperbarui" : "Supplier ditambahkan");
      setDlg(false); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const del = async (id) => { if (!window.confirm("Hapus data ini?")) return; try { await api.delete(`/suppliers/${id}`); toast.success("Dihapus"); load(); } catch (e) { toast.error(apiError(e)); } };

  const filtered = rows.filter((r) => {
    const s = q.trim().toLowerCase();
    const matchQ = !s || [r.material, r.supplier_name, r.sales_name, r.phone, r.supplier_address, r.category].some((v) => (v || "").toLowerCase().includes(s));
    const matchCat = cat === "all" || (r.category || "") === cat;
    return matchQ && matchCat;
  });

  return (
    <div className="space-y-4" data-testid="supplier-panel">
      <Card className="card-surface rounded-2xl p-5">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2 font-display text-lg font-bold text-neutral-900"><Package className="text-crimson" size={18} /> Supplier ({rows.length})</div>
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto">
            <Select value={cat} onValueChange={setCat}>
              <SelectTrigger data-testid="supplier-cat-filter" className="w-full bg-neutral-100 border-neutral-200 sm:w-56"><SelectValue placeholder="Semua Kategori" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">Semua Kategori</SelectItem>
                {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="relative flex-1 sm:w-72">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
              <Input data-testid="supplier-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Cari barang / supplier / sales / No HP" className="bg-neutral-100 border-neutral-200 pl-9" />
            </div>
            <Button data-testid="supplier-add-btn" onClick={openAdd} className="bg-crimson font-bold hover:bg-red-500"><Plus size={16} className="mr-1" /> Tambah</Button>
          </div>
        </div>
        <div className="space-y-2" data-testid="supplier-list">
          {filtered.length === 0 && <p className="text-sm text-muted-foreground">{rows.length === 0 ? "Belum ada data supplier." : "Tidak ada hasil untuk filter ini."}</p>}
          {filtered.map((r) => (
            <div key={r.id} data-testid={`supplier-row-${r.id}`} className="flex flex-wrap items-start justify-between gap-3 rounded-xl bg-neutral-100 px-4 py-3">
              <div className="min-w-[200px] flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="font-semibold text-neutral-900">{r.material}</span>
                  {r.category && <span className="inline-flex items-center gap-1 rounded-full bg-crimson/10 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide text-crimson"><Tag size={10} /> {r.category}</span>}
                </div>
                <div className="mt-0.5 flex flex-wrap gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                  {r.supplier_name && <span className="flex items-center gap-1"><Package size={12} /> {r.supplier_name}</span>}
                  {r.sales_name && <span className="flex items-center gap-1"><User size={12} /> {r.sales_name}</span>}
                  {r.phone && <span className="flex items-center gap-1"><Phone size={12} /> {r.phone}</span>}
                  {r.supplier_address && <span className="flex items-center gap-1"><MapPin size={12} /> {r.supplier_address}</span>}
                </div>
                {r.sop && <div className="mt-1.5 flex items-start gap-1 whitespace-pre-wrap rounded-lg bg-white/70 px-2 py-1 text-xs text-neutral-700"><FileText size={12} className="mt-0.5 shrink-0 text-crimson" /> <span>{r.sop}</span></div>}
              </div>
              <div className="flex items-center gap-1">
                <Button data-testid={`supplier-edit-${r.id}`} onClick={() => openEdit(r)} variant="ghost" size="icon" className="text-gold hover:bg-gold/10"><Pencil size={16} /></Button>
                <Button data-testid={`supplier-del-${r.id}`} onClick={() => del(r.id)} variant="ghost" size="icon" className="text-muted-foreground hover:text-crimson"><Trash2 size={16} /></Button>
              </div>
            </div>
          ))}
        </div>
      </Card>

      <Dialog open={dlg} onOpenChange={setDlg}>
        <DialogContent className="glass border-neutral-200 text-neutral-900">
          <DialogHeader><DialogTitle className="font-display">{editId ? "Edit" : "Tambah"} Supplier</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <Input data-testid="sup-material" placeholder="Nama Bahan / Barang" value={form.material} onChange={(e) => setForm({ ...form, material: e.target.value })} className="bg-neutral-100 border-neutral-200" />
            <div>
              <label className="mb-1 block text-xs font-semibold text-muted-foreground">Kategori</label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="sup-category" className="bg-neutral-100 border-neutral-200"><SelectValue placeholder="Pilih kategori" /></SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <Input data-testid="sup-name" placeholder="Nama Supplier" value={form.supplier_name} onChange={(e) => setForm({ ...form, supplier_name: e.target.value })} className="bg-neutral-100 border-neutral-200" />
            <Input data-testid="sup-address" placeholder="Alamat Supplier" value={form.supplier_address} onChange={(e) => setForm({ ...form, supplier_address: e.target.value })} className="bg-neutral-100 border-neutral-200" />
            <Input data-testid="sup-sales" placeholder="Nama Sales" value={form.sales_name} onChange={(e) => setForm({ ...form, sales_name: e.target.value })} className="bg-neutral-100 border-neutral-200" />
            <Input data-testid="sup-phone" placeholder="Nomor HP" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="bg-neutral-100 border-neutral-200" />
            <Textarea data-testid="sup-sop" placeholder="Keterangan SOP (mis. cara penyimpanan, kualitas yang diterima, prosedur penerimaan barang, dll.)" value={form.sop} onChange={(e) => setForm({ ...form, sop: e.target.value })} rows={6} className="min-h-[140px] bg-neutral-100 border-neutral-200" />
            <Button data-testid="sup-save-btn" onClick={save} className="w-full bg-crimson font-bold hover:bg-red-500">Simpan</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
